/**
 * User Settings Service
 * API client for user settings operations
 */

import apiClient from './api.client';
import type { User } from '@/types/api.types';

export interface UserSettings {
  id: string;
  user_id: string;

  // Profile
  bio: string | null;
  status: string | null;
  status_message: string | null;

  // Notifications
  notification_preference: string;
  desktop_notifications_enabled: boolean;
  sound_notifications_enabled: boolean;
  email_notifications_enabled: boolean;
  notification_sound: string;
  sound_volume: number;
  muted_until: string | null;

  // Per-notification-type sound settings
  message_sound: string;
  mention_sound: string;
  dm_sound: string;
  system_sound: string;
  message_volume: number;
  mention_volume: number;
  dm_volume: number;
  system_volume: number;

  // Voice settings
  voice_input_device: string | null;
  voice_output_device: string | null;
  noise_suppression: boolean;
  echo_cancellation: boolean;
  auto_gain_control: boolean;
  push_to_talk_enabled: boolean;
  push_to_talk_key: string;
  voice_join_sound: string;
  voice_leave_sound: string;
  voice_join_volume: number;
  voice_leave_volume: number;
  call_ringtone: string;
  call_ringtone_volume: number;

  // Display
  theme: string;
  font_size: string;
  high_contrast_mode: boolean;
  message_display: string;
  show_avatars: boolean;
  show_link_previews: boolean;
  show_link_preview_images: boolean;
  sidebar_layout: string;

  // Privacy
  show_last_seen: boolean;
  show_read_receipts: boolean;
  profile_visibility: string;

  // Preferences
  language: string | null;
  timezone: string | null;
  emoji_style: string;
  enter_key_behavior: string;
  keyboard_shortcuts_enabled: boolean;
  custom_shortcuts: Record<string, unknown> | null;

  // Input
  paste_as_file_threshold: number;

  // Metadata
  created_at: string;
  updated_at: string;
}

export interface PartialUserSettings {
  bio?: string | null;
  status?: string | null;
  status_message?: string | null;
  notification_preference?: string;
  desktop_notifications_enabled?: boolean;
  sound_notifications_enabled?: boolean;
  email_notifications_enabled?: boolean;
  notification_sound?: string;
  sound_volume?: number;
  muted_until?: string | null;

  // Per-notification-type sound settings
  message_sound?: string;
  mention_sound?: string;
  dm_sound?: string;
  system_sound?: string;
  message_volume?: number;
  mention_volume?: number;
  dm_volume?: number;
  system_volume?: number;

  // Voice settings
  voice_input_device?: string | null;
  voice_output_device?: string | null;
  noise_suppression?: boolean;
  echo_cancellation?: boolean;
  auto_gain_control?: boolean;
  push_to_talk_enabled?: boolean;
  push_to_talk_key?: string;
  voice_join_sound?: string;
  voice_leave_sound?: string;
  voice_join_volume?: number;
  voice_leave_volume?: number;
  call_ringtone?: string;
  call_ringtone_volume?: number;

  theme?: string;
  font_size?: string;
  high_contrast_mode?: boolean;
  message_display?: string;
  show_avatars?: boolean;
  show_link_previews?: boolean;
  show_link_preview_images?: boolean;
  sidebar_layout?: string;
  show_last_seen?: boolean;
  show_read_receipts?: boolean;
  profile_visibility?: string;
  language?: string | null;
  timezone?: string | null;
  emoji_style?: string;
  enter_key_behavior?: string;
  keyboard_shortcuts_enabled?: boolean;
  custom_shortcuts?: Record<string, unknown> | null;
  paste_as_file_threshold?: number;
}

/**
 * Get current user's settings
 */
export const getUserSettings = async (): Promise<UserSettings> => {
  const response = await apiClient.get('/user-settings');
  return response.data.data.settings;
};

/**
 * Update current user's settings (partial update)
 */
export const updateUserSettings = async (
  updates: PartialUserSettings
): Promise<UserSettings> => {
  const response = await apiClient.patch('/user-settings', updates);
  return response.data.data.settings;
};

/**
 * Update the current user's presence status (feature #43).
 * Persists on the server (user_settings) and triggers a `status:update`
 * broadcast so colleagues see the change in real time.
 */
export const updateStatus = async (
  status: 'online' | 'away' | 'dnd' | 'offline',
  statusMessage?: string
): Promise<{ status: string; statusMessage: string | null }> => {
  const response = await apiClient.patch('/users/status', { status, statusMessage });
  return response.data.data;
};

/**
 * Do Not Disturb (DND) API
 */

export interface DNDStatus {
  enabled: boolean;
  mutedUntil: string | null;
  isIndefinite: boolean;
}

/**
 * Enable Do Not Disturb mode
 * @param duration - Duration in minutes (optional, omit for indefinite)
 */
export const enableDND = async (duration?: number): Promise<{ settings: UserSettings; dndEnabled: boolean; mutedUntil: string }> => {
  const response = await apiClient.post('/user-settings/dnd/enable', { duration });
  return response.data.data;
};

/**
 * Disable Do Not Disturb mode
 */
export const disableDND = async (): Promise<{ settings: UserSettings; dndEnabled: boolean }> => {
  const response = await apiClient.post('/user-settings/dnd/disable');
  return response.data.data;
};

/**
 * Get DND status
 */
export const getDNDStatus = async (): Promise<DNDStatus> => {
  const response = await apiClient.get('/user-settings/dnd/status');
  return response.data.data;
};

/**
 * Upload user avatar
 */
export const uploadAvatar = async (file: File): Promise<User> => {
  const formData = new FormData();
  formData.append('avatar', file);

  // CRITICAL: Delete Content-Type header so axios can set multipart/form-data with boundary
  const response = await apiClient.post('/users/avatar', formData, {
    headers: {
      'Content-Type': undefined, // This forces axios to set the correct multipart boundary
    },
  });

  return response.data.data.user;
};

/**
 * Delete user avatar
 */
export const deleteAvatar = async (): Promise<User> => {
  const response = await apiClient.delete('/users/avatar');
  return response.data.data.user;
};

/**
 * Update user profile
 */
export const updateProfile = async (updates: {
  display_name?: string;
}): Promise<User> => {
  const response = await apiClient.patch('/users/profile', updates);
  return response.data.data.user;
};
